import { useEffect, useState } from "react";
import { Share, PlusSquare, MoreVertical, X, Download } from "lucide-react";
import { isNative } from "../../lib/native";

const KEY = "zunii_install_dismissed_at";
const SNOOZE_DAYS = 14;

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || navigator.standalone === true;
const isMobile = () => window.matchMedia("(pointer: coarse)").matches || window.innerWidth < 1024;
const snoozed = () => Date.now() - Number(localStorage.getItem(KEY) || 0) < SNOOZE_DAYS * 864e5;

export const InstallPrompt = () => {
  const [show, setShow] = useState(false);
  const [evt, setEvt] = useState(() => window.__zuniiInstallEvent || null);

  useEffect(() => {
    if (isNative || isStandalone() || !isMobile() || snoozed()) return;
    const onReady = () => setEvt(window.__zuniiInstallEvent);
    const onInstalled = () => { localStorage.setItem(KEY, String(Date.now() + 3650 * 864e5)); setShow(false); };
    window.addEventListener("zunii:installable", onReady);
    window.addEventListener("appinstalled", onInstalled);
    const t = setTimeout(() => setShow(true), 1500);
    return () => { clearTimeout(t); window.removeEventListener("zunii:installable", onReady); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  const dismiss = () => { localStorage.setItem(KEY, String(Date.now())); setShow(false); };
  const install = async () => {
    evt.prompt();
    const { outcome } = await evt.userChoice;
    window.__zuniiInstallEvent = null;
    if (outcome === "accepted") setShow(false);
    else dismiss();
  };

  if (!show) return null;
  const ios = isIOS();

  return (
    <div data-testid="install-prompt" className="lg:hidden fixed inset-x-3 z-50 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] zn-card zn-pop bg-white p-4">
      <button data-testid="install-dismiss-btn" onClick={dismiss} className="absolute top-2 right-2 w-9 h-9 grid place-items-center rounded-full hover:bg-[#FFF7EC]" aria-label="Dismiss"><X size={18} /></button>
      <div className="flex gap-3 pr-8">
        <img src="/icon-192.png" alt="" className="w-14 h-14 rounded-2xl border-[3px] border-[#1B1A2E] shrink-0" />
        <div className="min-w-0">
          <p className="font-display text-lg font-bold leading-tight">Add Zunii to your home screen</p>
          <p className="text-sm text-[#1B1A2E]/70">One tap to approve videos and check on your kids, just like an app.</p>
        </div>
      </div>
      {evt ? (
        <div className="flex gap-2 mt-4">
          <button data-testid="install-accept-btn" onClick={install} className="zn-btn bg-[#FF6B4A] text-white flex-1 !py-2"><Download size={18} /> Add to home screen</button>
          <button data-testid="install-later-btn" onClick={dismiss} className="zn-btn bg-white !py-2">Not now</button>
        </div>
      ) : (
        <ol data-testid={ios ? "install-steps-ios" : "install-steps-other"} className="mt-3 space-y-2 text-sm">
          {ios ? (
            <>
              <li className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-[#FFD23F] border-2 border-[#1B1A2E] grid place-items-center text-xs font-bold shrink-0">1</span> Tap <Share size={16} className="inline text-[#007AFF]" /> <b>Share</b> in Safari's toolbar</li>
              <li className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-[#FFD23F] border-2 border-[#1B1A2E] grid place-items-center text-xs font-bold shrink-0">2</span> Choose <PlusSquare size={16} className="inline" /> <b>Add to Home Screen</b></li>
            </>
          ) : (
            <>
              <li className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-[#FFD23F] border-2 border-[#1B1A2E] grid place-items-center text-xs font-bold shrink-0">1</span> Open your browser menu <MoreVertical size={16} className="inline" /></li>
              <li className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-[#FFD23F] border-2 border-[#1B1A2E] grid place-items-center text-xs font-bold shrink-0">2</span> Tap <b>Add to Home screen</b> or <b>Install app</b></li>
            </>
          )}
          <li><button data-testid="install-later-btn" onClick={dismiss} className="text-sm font-semibold underline mt-1">Maybe later</button></li>
        </ol>
      )}
    </div>
  );
};
